import React, { useEffect, useState, useMemo } from 'react';
import {
  Card, Typography, Space, Button,
   Avatar, Spin, Form, Input, DatePicker, message,
    Tabs, Empty, Upload, Table, Select, Pagination
} from 'antd';
import {
  ArrowLeftOutlined, EditOutlined, SaveOutlined, CloseOutlined,
   MailOutlined, PhoneOutlined, CalendarOutlined,
  IdcardOutlined, CheckCircleOutlined, ClockCircleOutlined,
  FileTextOutlined, CameraOutlined, DeleteOutlined,
  HistoryOutlined, TeamOutlined, SolutionOutlined, EnvironmentOutlined, EyeOutlined,
  SearchOutlined, ThunderboltOutlined, LeftOutlined, RightOutlined,
  StarOutlined  
} from '@ant-design/icons';
import { RxAvatar } from "react-icons/rx";
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { userActions } from '../../actions/userActions';
import { commonActions } from '../../actions/commonActions';
import { getblobLink } from '../../services/helper';
import dayjs from 'dayjs';
import GuardedButton from '../../components/guardedButton';
import { useCurrentPageAccess } from '../../services/helper';
import InstructorRatingsTab from '../../components/InstructorRatingsTab';
import SpinnerLoader from '../../components/spinnerLoader';

const {  Text } = Typography;

const ENROLL_PAGE_SIZE = 5;

// ─── History helpers ──────────────────────────────────────────────────────────
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
const fmtRelative = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return fmtDate(iso);
};

const HISTORY_EVENT_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string; bg: string; border: string; category: string; description: (r: any) => string }> = {
  BATCH_ASSIGNED: {
    label: 'Batch Assigned', icon: <SolutionOutlined />,
    color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe', category: 'assignments',
    description: (r) => `Instructor was assigned to batch${r.batchName || r.batchDetails?.batchName ? ` "${r.batchName || r.batchDetails?.batchName}"` : ''}.`,
  },
  BATCH_REMOVED: {
    label: 'Batch Removed', icon: <DeleteOutlined />,
    color: '#be123c', bg: '#fff1f2', border: '#fecdd3', category: 'assignments',
    description: (r) => `Instructor was removed from batch${r.batchName || r.batchDetails?.batchName ? ` "${r.batchName || r.batchDetails?.batchName}"` : ''}.`,
  },
  BATCH_COMPLETED: {
    label: 'Batch Completed', icon: <CheckCircleOutlined />,
    color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', category: 'batch_updates',
    description: (r) => `Batch${r.batchName || r.batchDetails?.batchName ? ` "${r.batchName || r.batchDetails?.batchName}"` : ''} has been marked as completed.`,
  },
  SESSION_CONDUCTED: {
    label: 'Attendance Marked', icon: <TeamOutlined />,
    color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc', category: 'attendance',
    description: (r) => {
      const present = r.attendanceRecords?.filter((a: any) => a.status === 'PRESENT').length || 0;
      const total = r.attendanceRecords?.length || r.studentsCount || 0;
      return `Session conducted${r.batchName || r.batchDetails?.batchName ? ` for "${r.batchName || r.batchDetails?.batchName}"` : ''}. ${present} of ${total} students marked present.`;
    },
  },
};

const HISTORY_FILTER_CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'assignments', label: 'Assignments' },
  { key: 'attendance', label: 'Attendance' },
];

const HistoryTimelineItem: React.FC<{ record: any; isLast: boolean }> = ({ record, isLast }) => {
  const cfg = HISTORY_EVENT_CONFIG[record.type] || {
    label: record.type || 'Activity', icon: <HistoryOutlined />,
    color: '#64748b', bg: '#f8fafc', border: '#e2e8f0',
    description: () => 'An activity was recorded.',
  };
  const hasAttendance = record.attendanceRecords?.length > 0;
  const presentCount = record.attendanceRecords?.filter((a: any) => a.status === 'PRESENT').length || 0;
  const absentCount = record.attendanceRecords?.filter((a: any) => a.status === 'ABSENT').length || 0;
  const batchName = record.batchName || record.batchDetails?.batchName;

  return (
    <div className="bh2-item">
      <div className="bh2-spine">
        <div className="bh2-node" style={{ background: cfg.bg, border: `2px solid ${cfg.border}`, color: cfg.color }}>
          {cfg.icon}
        </div>
        {!isLast && <div className="bh2-spine-line" />}
      </div>
      <div className="bh2-card">
        <div className="bh2-card-head">
          <div className="bh2-card-head-left">
            <span className="bh2-event-tag" style={{ color: cfg.color }}>{cfg.label.toUpperCase()}</span>
          </div>
          {record.createdAt && (
            <div className="bh2-timestamp">
              <ClockCircleOutlined className="bh2-ts-icon" />
              <span className="bh2-ts-rel">{fmtRelative(record.createdAt)}</span>
              <span className="bh2-ts-full">{fmtDate(record.createdAt)} · {fmtTime(record.createdAt)}</span>
            </div>
          )}
        </div>
        <p className="bh2-desc">{(cfg.description as Function)(record)}</p>
        {batchName && (
          <div className="bh2-actor">
            <Avatar size={22} icon={<TeamOutlined />} className="bh2-actor-avatar" />
            <span className="bh2-actor-name">{batchName}</span>
            <span className="bh2-actor-role">Batch</span>
          </div>
        )}
        {record.sessionDate && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 12, color: '#15803d', marginTop: 6 }}>
            <CalendarOutlined />
            <strong>{new Date(record.sessionDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>
          </div>
        )}
        {hasAttendance && (
          <div style={{ marginTop: 10 }}>
            <div className="bh2-att-summary">
              <span className="bh2-att-chip bh2-att-chip--present"><span className="bh2-att-dot bh2-att-dot--present" />{presentCount} Present</span>
              <span className="bh2-att-chip bh2-att-chip--absent"><span className="bh2-att-dot bh2-att-dot--absent" />{absentCount} Absent</span>
              <span className="bh2-att-chip bh2-att-chip--total">{record.attendanceRecords.length} Total</span>
            </div>
            <div className="bh2-student-pills" style={{ marginTop: 8 }}>
              {record.attendanceRecords.map((ar: any, i: number) => {
                const isPresent = ar.status === 'PRESENT';
                return (
                  <span key={i} className={`bh2-student-pill bh2-student-pill--${isPresent ? 'present' : 'absent'}`}>
                    <span className={`bh2-att-dot bh2-att-dot--${isPresent ? 'present' : 'absent'}`} />
                    {ar.studentName || 'Student'}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const HistorySummaryCard: React.FC<{ list: any[] }> = ({ list }) => {
  const first = list.length ? list[list.length - 1] : null;
  const latest = list.length ? list[0] : null;
  const sessionsTotal = list.filter((r) => r.type === 'SESSION_CONDUCTED').length;
  const batchesTotal = list.filter((r) => r.type === 'BATCH_ASSIGNED').length;
  const rows = [
    { label: 'First Activity', value: first?.createdAt ? fmtDate(first.createdAt) : '—', icon: <CalendarOutlined /> },
    { label: 'Latest Activity', value: latest?.createdAt ? fmtDate(latest.createdAt) : '—', icon: <ClockCircleOutlined /> },
    { label: 'Total Activities', value: list.length, icon: <HistoryOutlined /> },
    { label: 'Sessions Conducted', value: sessionsTotal, icon: <TeamOutlined /> },
    { label: 'Batches Assigned', value: batchesTotal, icon: <SolutionOutlined /> },
  ];
  return (
    <Card className="bh2-sidebar-card" size="small">
      <div className="bh2-sidebar-card-title"><ThunderboltOutlined /> Summary</div>
      <div className="bh2-summary-rows">
        {rows.map((r) => (
          <div key={r.label} className="bh2-summary-row">
            <span className="bh2-summary-icon">{r.icon}</span>
            <span className="bh2-summary-label">{r.label}</span>
            <span className="bh2-summary-value">{r.value}</span>
          </div>
        ))}
      </div>
    </Card>
  );
};

const HistoryEventTypesCard: React.FC<{ list: any[] }> = ({ list }) => {
  const countMap: Record<string, number> = {};
  list.forEach((r) => { countMap[r.type] = (countMap[r.type] || 0) + 1; });
  const entries = Object.entries(countMap).sort((a, b) => b[1] - a[1]);
  const total = list.length || 1;
  return (
    <Card className="bh2-sidebar-card" size="small">
      <div className="bh2-sidebar-card-title"><HistoryOutlined /> Event Types</div>
      {entries.length === 0
        ? <Text type="secondary" style={{ fontSize: 12 }}>No events</Text>
        : entries.map(([type, count]) => {
          const cfg = HISTORY_EVENT_CONFIG[type];
          const pct = Math.round((count / total) * 100);
          return (
            <div key={type} className="bh2-etype-row">
              <span className="bh2-etype-dot" style={{ background: cfg?.color || '#94a3b8' }} />
              <span className="bh2-etype-label">{cfg?.label || type}</span>
              <div className="bh2-etype-bar-wrap">
                <div className="bh2-etype-bar" style={{ width: `${pct}%`, background: cfg?.color || '#94a3b8' }} />
              </div>
              <span className="bh2-etype-count">{count}</span>
            </div>
          );
        })
      }
    </Card>
  );
};

// ─── GreytHR-inspired Attendance Calendar ────────────────────────────────────
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const AttendanceCalendarView: React.FC<{
  historyList: any[];
  user: any;
  isLoading: boolean;
}> = ({ historyList, user, isLoading }) => {
  const today = new Date();
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const absentSet = useMemo(() => {
    const s = new Set<string>();
    (user?.upcomingAbsentDates || []).forEach((d: string) => s.add(new Date(d).toDateString()));
    return s;
  }, [user]);

  const sessionsByDate = useMemo(() => {
    const map: Record<string, any[]> = {};
    historyList.filter(r => r.type === 'SESSION_CONDUCTED').forEach(r => {
      if (r.sessionDate) {
        const key = new Date(r.sessionDate).toDateString();
        if (!map[key]) map[key] = [];
        map[key].push(r);
      }
    });
    return map;
  }, [historyList]);

  const calDays = useMemo(() => {
    const firstDay = new Date(calYear, calMonth, 1).getDay();
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const cells: Array<{ day: number | null; date: Date | null }> = [];
    for (let i = 0; i < firstDay; i++) cells.push({ day: null, date: null });
    for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, date: new Date(calYear, calMonth, d) });
    return cells;
  }, [calMonth, calYear]);

  const prevMonth = () => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); };
  const nextMonth = () => { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); };

  const monthSessions = historyList.filter(r => {
    if (r.type !== 'SESSION_CONDUCTED' || !r.sessionDate) return false;
    const d = new Date(r.sessionDate);
    return d.getMonth() === calMonth && d.getFullYear() === calYear;
  });

  const monthAbsences = (user?.upcomingAbsentDates || []).filter((d: string) => {
    const dt = new Date(d);
    return dt.getMonth() === calMonth && dt.getFullYear() === calYear;
  });

  const selectedSessions = selectedDate ? (sessionsByDate[selectedDate.toDateString()] || []) : [];
  const selectedIsAbsent = selectedDate ? absentSet.has(selectedDate.toDateString()) : false;

  // Recent absence history feed
  const absenceHistory = (user?.upcomingAbsentDates || [])
    .map((d: string) => new Date(d))
    .sort((a: Date, b: Date) => b.getTime() - a.getTime())
    .slice(0, 8);

  return (
    <div className="gthr-att-root">
      {/* ── LEFT PANEL ── */}
      <div className="gthr-left-panel">
        {/* Mini stats */}



        {/* Absence history feed */}
        <div className="gthr-abs-feed">
          <div className="gthr-abs-feed-title">
            <span className="gthr-abs-feed-icon"><CalendarOutlined /></span>
             History
            {absenceHistory.length > 0 && <span className="gthr-abs-feed-count">{absenceHistory.length}</span>}
          </div>

          {/* ── Selected date detail (shown inside left panel) ── */}


          {absenceHistory.length === 0 ? (
            <div className="gthr-abs-feed-empty">
              <CheckCircleOutlined style={{ color: '#22c55e', fontSize: 18 }} />
              <span>No Unavailable dates scheduled</span>
            </div>
          ) : (
            <div className="gthr-abs-feed-list">
              {absenceHistory.map((d: Date, i: number) => (
                <div
                  key={i}
                  className={`gthr-abs-feed-item ${selectedDate?.toDateString() === d.toDateString() ? 'gthr-abs-feed-item--active' : ''}`}
                  onClick={() => { setCalMonth(d.getMonth()); setCalYear(d.getFullYear()); setSelectedDate(d); }}
                >
                  <div className="gthr-abs-feed-badge">
                    <span className="gthr-abs-a-marker">UA</span>
                  </div>
                  <div className="gthr-abs-feed-item-info">
                    <span className="gthr-abs-feed-item-day">{d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                    <span className="gthr-abs-feed-item-year">{d.getFullYear()}</span>
                  </div>
                  <div className={`gthr-abs-feed-item-chip ${d >= today ? 'gthr-abs-chip--future' : 'gthr-abs-chip--past'}`}>
                    {d >= today ? 'Upcoming' : 'Past'}
                  </div>
                </div>
              ))}
            </div>
          )}
          {selectedDate && (
            <div style={{marginTop:'10px'}}>
              <span className="gthr-abs-feed-icon">
                <CalendarOutlined />
                <span style={{ marginLeft: "5px", fontWeight:"700", color:"#374151"}}>Batch Info</span>
              </span>

              <div className="gthr-left-detail-card">
                <div className="gthr-left-detail-header">

                  <div className="gthr-left-detail-badge">
                    <span className="gthr-left-detail-day">{selectedDate.getDate()}</span>
                    <span className="gthr-left-detail-mon">{MONTHS[selectedDate.getMonth()].slice(0, 3).toUpperCase()}</span>
                  </div>
                  <div className="gthr-left-detail-meta">
                    <span className="gthr-left-detail-weekday">
                      {selectedDate.toLocaleDateString('en-IN', { weekday: 'long' })}
                    </span>
                    <span className="gthr-left-detail-fulldate">
                      {selectedDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  </div>
                </div>

                <div className="gthr-left-detail-chips">
                  {selectedIsAbsent && (
                    <span className="gthr-left-chip gthr-left-chip--absent">
                      <span className="gthr-left-chip-a">A</span> Absent
                    </span>
                  )}
                  {!selectedIsAbsent && selectedDate.toDateString() === today.toDateString() && (
                    <span className="gthr-left-chip gthr-left-chip--present">
                      <span className="gthr-left-present-dot" /> Present Today
                    </span>
                  )}
                  {selectedSessions.length > 0 && (
                    <span className="gthr-left-chip gthr-left-chip--session">
                      <TeamOutlined /> {selectedSessions.length} Session{selectedSessions.length > 1 ? 's' : ''}
                    </span>
                  )}
                  {!selectedIsAbsent && selectedSessions.length === 0 && selectedDate.toDateString() !== today.toDateString() && (
                    <span className="gthr-left-chip gthr-left-chip--nodata">No Records</span>
                  )}
                </div>

                {selectedSessions.length > 0 && (
                  <div className="gthr-left-detail-sessions">
                    {selectedSessions.map((s: any, i: number) => {
                      const present = s.attendanceRecords?.filter((a: any) => a.status === 'PRESENT').length || 0;
                      const total = s.attendanceRecords?.length || 0;
                      const pct = total > 0 ? Math.round((present / total) * 100) : 0;
                      const color = pct >= 75 ? '#16a34a' : pct >= 50 ? '#b45309' : '#dc2626';
                      const bg = pct >= 75 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#ef4444';
                      return (
                        <div key={i} className="gthr-left-session-row">
                          <div className="gthr-left-session-top">
                            <span className="gthr-left-session-batch">
                              {s.batchName || s.batchDetails?.batchName || 'Batch'}
                            </span>
                            <span className="gthr-left-session-pct" style={{ color }}>{pct}%</span>
                          </div>
                          <div className="gthr-left-session-bar-track">
                            <div className="gthr-left-session-bar-fill" style={{ width: `${pct}%`, background: bg }} />
                          </div>
                          <span className="gthr-left-session-meta">
                            {present} present · {total - present} absent
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── MAIN CALENDAR ── */}
      <div className="gthr-main-cal">
        {/* Calendar header */}
        <div className="gthr-cal-header">
          <div className="gthr-cal-header-left">
            <button className="gthr-cal-nav-btn" onClick={prevMonth}><LeftOutlined /></button>
            <div className="gthr-cal-month-info">
              <span className="gthr-cal-month-name">{MONTHS[calMonth]}</span>
              <span className="gthr-cal-year">{calYear}</span>
            </div>
            <button className="gthr-cal-nav-btn" onClick={nextMonth}><RightOutlined /></button>
            <button className="gthr-cal-today-btn" onClick={() => { setCalMonth(today.getMonth()); setCalYear(today.getFullYear()); setSelectedDate(today); }}>
              Today
            </button>
          </div>
          <div className="gthr-cal-month-stats">
            <span className="gthr-cal-mstat gthr-cal-mstat--session"><TeamOutlined /> {monthSessions.length} sessions</span>
            <span className="gthr-cal-mstat gthr-cal-mstat--absent"><span className="gthr-a-dot">UA</span> {monthAbsences.length} Unavailable </span>
          </div>
        </div>

        {/* DOW row */}
        <div className="gthr-cal-dow-row">
          {DOW.map(d => <div key={d} className="gthr-cal-dow">{d}</div>)}
        </div>

        {/* Calendar grid */}
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spin size="large" /></div>
        ) : (
          <div className="gthr-cal-grid">
            {Array.from({ length: Math.ceil(calDays.length / 7) }, (_, wi) => (
              <div key={wi} className="gthr-cal-week">
                {calDays.slice(wi * 7, wi * 7 + 7).map((cell, ci) => {
                  if (!cell.date) return <div key={ci} className="gthr-cal-cell gthr-cal-cell--empty" />;
                  const isToday = cell.date.toDateString() === today.toDateString();
                  const isAbsent = absentSet.has(cell.date.toDateString());
                  const sessions = sessionsByDate[cell.date.toDateString()] || [];
                  const hasSessions = sessions.length > 0;
                  const isSelected = selectedDate?.toDateString() === cell.date.toDateString();

                  return (
                    <div
                      key={ci}
                      className={[
                        'gthr-cal-cell',
                        isToday ? 'gthr-cal-cell--today' : '',
                        isAbsent ? 'gthr-cal-cell--absent' : '',
                        hasSessions ? 'gthr-cal-cell--session' : '',
                        isSelected ? 'gthr-cal-cell--selected' : '',
                      ].join(' ')}
                      onClick={() => setSelectedDate(cell.date)}
                    >
                      <div className="gthr-cal-day-num-row">
                        <span className={`gthr-cal-day-num ${isToday ? 'gthr-cal-day-num--today' : ''}`}>
                          {cell.day}
                        </span>
                        {isAbsent && (
                          <span className="gthr-cal-absent-marker">
                            <span className="gthr-cal-a-letter">A</span>
                          </span>
                        )}
                      </div>
                      {hasSessions && (
                        <div className="gthr-cal-session-chips">
                          {sessions.slice(0, 2).map((s, si) => {
                            const present = s.attendanceRecords?.filter((a: any) => a.status === 'PRESENT').length || 0;
                            const total = s.attendanceRecords?.length || 0;
                            return (
                              <div key={si} className="gthr-cal-session-chip">
                                <span className="gthr-cal-session-dot" />
                                <span className="gthr-cal-session-txt">{present}/{total}</span>
                              </div>
                            );
                          })}
                          {sessions.length > 2 && (
                            <div className="gthr-cal-more-chip">+{sessions.length - 2}</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}


      </div>
    </div>
  );
};

const UserView: React.FC = () => {
  const location = useLocation();
  const userId = location.state?.userId;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pageAccess = useCurrentPageAccess();
  const [form] = Form.useForm();
  const [isEditMode, setIsEditMode] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState<string>('');
  const [imageDeleted, setImageDeleted] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadedImageId, setUploadedImageId] = useState<string | null>(null);
  
  const [historyTypeFilter, setHistoryTypeFilter] = useState<string | undefined>(undefined);
  const [enrollPage, setEnrollPage] = useState(1);
  const [historySearch, setHistorySearch] = useState('');
  const [historyActiveFilter, setHistoryActiveFilter] = useState('all');

  const userViewState = useSelector((state: any) => state.userViewReducer);
  const userUpdateState = useSelector((state: any) => state.userUpdateReducer);
  const instructorHistoryState = useSelector((state: any) => state.getInstructorHistoryReducer);
  const uploadState = useSelector((state: any) => state.updateProfileImageReducer);
  const deleteFilesState = useSelector((state: any) => state.deleteFilesReducer);

  const loggedInAuthData = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('AUTHDATA') || '{}'); } catch { return {}; }
  }, []);
  const isSelfProfile = Boolean(userId && loggedInAuthData?.id === userId);

  const isLoading = Boolean(userViewState?.isLoading);
  const isUpdating = Boolean(userUpdateState?.isLoading);
  const user = userViewState?.userData ?? null;

  const createdDateObj: Date | null = (() => {
    const v: any = user?.createdAt;
    if (!v) return null;
    const num = Number(v);
    if (!Number.isNaN(num)) {
      if (String(v).length === 10) return new Date(num * 1000);
      return new Date(num);
    }
    return new Date(String(v));
  })();

  useEffect(() => {
    if (userId) {
      dispatch(userActions.userViewRequest({ id: userId }));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        firstName: user.firstName,
        lastName: user.lastName,
        address: user.address,
        dateOfBirth: user.dateOfBirth ? dayjs(user.dateOfBirth) : null,
        gender: user.gender,
      });
      const imgUrl = user?.profileImageUrl || user?.profileImage?.url || '';
      if (imgUrl) {
        setProfileImageUrl(imgUrl);
      }  else {
        setProfileImageUrl('');
      }
      setImageDeleted(false);
      setUploadedImageId(null);
    }
  }, [user, form]);

  useEffect(() => {
    if (userUpdateState?.isSuccess) {
      setIsEditMode(false);
      setImageDeleted(false);
      setUploadedImageId(null);
      userUpdateState.isSuccess = false;
      if (userId) dispatch(userActions.userViewRequest({ id: userId }));
      if (isSelfProfile) dispatch(userActions.getProfileRequest({ id: userId }));
    }
  }, [userUpdateState, dispatch, userId, isSelfProfile]);

  useEffect(() => {
    if (uploadState?.isSuccess) {
      const uploadedFile = uploadState?.profileImage;
      if (uploadedFile && uploadedFile._id) {
        setUploadedImageId(uploadedFile._id);
        setProfileImageUrl(uploadedFile.url || '');
      }
      setIsUploadingImage(false);
      setIsEditMode(false);
      setUploadedImageId(null);
      if (isSelfProfile) dispatch(userActions.getProfileRequest({ id: userId }));
    }
  }, [uploadState, isSelfProfile, dispatch, userId]);

  useEffect(() => {
    if (deleteFilesState?.isSuccess) {
      if (userId) dispatch(userActions.userViewRequest({ id: userId }));
      if (isSelfProfile) dispatch(userActions.getProfileRequest({ id: userId }));
    }
  }, [deleteFilesState?.isSuccess, dispatch, userId, isSelfProfile]);

  const getRoleLabel = (r: any): string => {
    const val = typeof r === 'object' && r !== null ? (r.role ?? r.roleName ?? '') : (r ?? '');
    return val.toString().toUpperCase();
  };

  const getDisplayName = (u: any): string => {
    const name = (u?.name ?? `${u?.firstName ?? ''} ${u?.lastName ?? ''}`).toString().trim();
    return name || '-';
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'STUDENT': return { bg: '#e6fffb', border: '#87e8de', text: '#13c2c2' };
      case 'INSTRUCTOR': return { bg: '#f9f0ff', border: '#d3adf7', text: '#722ed1' };
      case 'STAFF': return { bg: '#f6ffed', border: '#b7eb8f', text: '#52c41a' };
      case 'ADMIN': return { bg: '#fff7e6', border: '#ffd591', text: '#fa8c16' };
      default: return { bg: '#f5f5f5', border: '#d9d9d9', text: '#595959' };
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'INACTIVE': return 'default';
      case 'SUSPENDED': return 'error';
      default: return 'default';
    }
  };

  const handleEditSave = async () => {
    try {
      const values = await form.validateFields();
      const payload: any = {
        ...values,
        dateOfBirth: values.dateOfBirth ? values.dateOfBirth.toISOString() : undefined,
        role: user?.role?._id,
        _id: user?._id,
        email: user?.email,
        phoneNumber: user?.phoneNumber,
      };


      if (!imageDeleted && uploadedImageId) {
        payload.profileImage = uploadedImageId;
      } else if (imageDeleted) {
        payload.profileImage = null;
      }

      dispatch(userActions.userUpdateRequest(payload));
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const beforeUpload = (file: File) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) { message.error('You can only upload image files!'); return Upload.LIST_IGNORE; }
    const isLt3M = file.size / 1024 / 1024 < 3;
    if (!isLt3M) { message.error('Image must be smaller than 3MB!'); return Upload.LIST_IGNORE; }

    
    setProfileImageUrl('');
    setImageDeleted(false);
    setIsUploadingImage(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'userProfile');
    formData.append('ReferenceId', String(user?._id || ''));
    dispatch(userActions.uploadProfileImageRequest(formData));
    return false;
  };

  const handleDeleteImage = () => {
    if (uploadedImageId) {
      dispatch(commonActions.deleteFiles({ ids: [uploadedImageId] }));
      setUploadedImageId(null);
    } else {
      const currentImage = user?.profileImage;
      const fileId = currentImage?._id || (typeof currentImage === 'string' ? currentImage : null);
      if (fileId) dispatch(commonActions.deleteFiles({ ids: [fileId] }));
    }
    setIsEditMode(false);
    setUploadedImageId(null);
    setProfileImageUrl('');
    setImageDeleted(true);
  };

  const handleCancelEdit = () => {   
    form.setFieldsValue({
      firstName: user?.firstName,
      lastName: user?.lastName,
      address: user?.address,
      dateOfBirth: user?.dateOfBirth ? dayjs(user.dateOfBirth) : null,
      gender: user?.gender,
    });
    const imgUrl = user?.profileImageUrl || user?.profileImage?.url || '';
    if (imgUrl) {
      setProfileImageUrl(imgUrl);
    } else if (user?.profileImage?.buffer) {
      setProfileImageUrl(`data:${user.profileImage.mimetype || 'image/jpeg'};base64,${user.profileImage.buffer}`);
    } else {
      setProfileImageUrl('');
    }
    setImageDeleted(false);
    setIsEditMode(false);
  };

  const getProfileImage = () => {
    if (isEditMode) {
      if (profileImageUrl) return profileImageUrl;
      if (imageDeleted) {
        const genderRaw = (user?.gender || '').toString().toLowerCase();
        return genderRaw === 'female' ? `${getblobLink()}/public/asset/female-avatar.png` : `${getblobLink()}/public/asset/male-avatar.png`;
      }
    }
  if (profileImageUrl) return profileImageUrl;
    const genderRaw = (user?.gender || '').toString().toLowerCase();
    return genderRaw === 'female' ? `${getblobLink()}/public/asset/female-avatar.png` : `${getblobLink()}/public/asset/male-avatar.png`;
  };

  const roleLabel = getRoleLabel(user?.role);
  const roleColors = getRoleColor(roleLabel);

  useEffect(() => {
    if (userId && roleLabel === 'INSTRUCTOR') {
      dispatch(userActions.getInstructorHistoryRequest({ instructorId: userId, type: historyTypeFilter }));
    }
  }, [userId, roleLabel, historyTypeFilter, dispatch]);

  const enrolments = user?.enrolments || [];

  if (isLoading) {
    return (
      <div className='view-loading'>
        <SpinnerLoader />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-view-empty">
        <Empty description="User not found" />
        <Button type="primary" onClick={() => navigate('/users')} style={{ marginTop: 16 }}>
          Back
        </Button>
      </div>
    );
  }

  const statusKey = (user?.status || 'ACTIVE').toUpperCase();
  const statusClass = statusKey === 'ACTIVE' ? 'active' : statusKey === 'SUSPENDED' ? 'danger' : 'inactive';
  const hasImage = profileImageUrl && !imageDeleted;
  const createdLabel = createdDateObj
    ? createdDateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;

  const formatAppStatus = (s?: string) => {
    if (!s) return 'Not Started';
    return s.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getDocsSummary = (docs: any[] = []) => {
    if (!Array.isArray(docs) || docs.length === 0) return { text: 'No Documents', cls: 'muted' };
    const missing = docs.filter((d) => {
      const st = (d?.status || '').toUpperCase();
      return !st || st === 'REJECTED' || st === 'MISSING';
    }).length;
    const pending = docs.filter((d) => (d?.status || '').toUpperCase() === 'PENDING').length;
    if (missing > 0) return { text: `${missing} Missing`, cls: 'missing' };
    if (pending > 0) return { text: `${pending} Pending`, cls: 'pending' };
    return { text: 'All Clear', cls: 'clear' };
  };

  const getEnrollStatusClass = (s?: string) => {
    switch ((s || '').toUpperCase()) {
      case 'COMPLETED': return 'completed';
      case 'ASSIGNED': return 'assigned';
      case 'IN_PROGRESS': return 'progress';
      case 'CANCELLED': return 'cancelled';
      default: return 'pending';
    }
  };

  const enrollmentColumns = [
    {
      title: 'Course', key: 'course',
      render: (_: any, e: any) => (
        <div className="uv-course-cell">
          <span className="uv-course-icon"><FileTextOutlined /></span>
          <span className="uv-course-text">
            <span className="uv-course-name">{e?.serviceName || e?.serviceId?.serviceName || 'Service'}</span>
            {(e?.categoryName || e?.categoryId?.categoryName) && (
              <span className="uv-course-cat">{e?.categoryName || e?.categoryId?.categoryName}</span>
            )}
          </span>
        </div>
      ),
    },
    {
      title: 'App Status', key: 'appStatus',
      render: (_: any, e: any) => <span className="uv-appstatus">{formatAppStatus(e?.applicationStatus)}</span>,
    },
    {
      title: 'Price', key: 'price',
      render: (_: any, e: any) => <span className="uv-price">₹{(e?.price || 0).toLocaleString()}</span>,
    },
    {
      title: 'Documents', key: 'documents',
      render: (_: any, e: any) => {
        const d = getDocsSummary(e?.documents);
        return <span className={`uv-doc-pill uv-doc-pill--${d.cls}`}>{d.text}</span>;
      },
    },
    {
      title: 'Status', key: 'status',
      render: (_: any, e: any) => (
        <span className={`uv-enroll-status uv-enroll-status--${getEnrollStatusClass(e?.status)}`}>
          {(e?.status || 'PENDING').replace(/_/g, ' ')}
        </span>
      ),
    },
    {
      title: '', key: 'action', width: 52, align: 'right' as const,
      render: (_: any, e: any) => (
        <Button type="text" size="small" className="uv-enroll-view" icon={<EyeOutlined />}
          onClick={() => navigate('/enrollments/view', { state: { enrollmentId: e?._id } })} />
      ),
    },
  ];

  const pagedEnrolments = enrolments.slice((enrollPage - 1) * ENROLL_PAGE_SIZE, enrollPage * ENROLL_PAGE_SIZE);

  const historyData = instructorHistoryState?.historyData;
  const historyList: any[] = historyData?.history || [];
  const isHistoryLoading = Boolean(instructorHistoryState?.isLoading);

  const filteredHistory = historyList.filter((r) => {
    const matchFilter = historyActiveFilter === 'all' || (HISTORY_EVENT_CONFIG[r.type]?.category || 'other') === historyActiveFilter;
    const q = historySearch.toLowerCase().trim();
    const batchName = (r.batchName || r.batchDetails?.batchName || '').toLowerCase();
    const label = (HISTORY_EVENT_CONFIG[r.type]?.label || '').toLowerCase();
    const matchSearch = !q || batchName.includes(q) || label.includes(q);
    return matchFilter && matchSearch;
  });


  return (
    <div className="batch-view-page user-view-page">
      {/* Header */}
      <div className="batch-view-header">
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/users')} className="batch-back-btn">Users</Button>
        <div className="batch-header-right">
          {createdLabel && <span className="batch-created">Created {createdLabel}</span>}
          {pageAccess.isEdit && (!isEditMode ? 
          ( user?.role?.role === "ADMIN" && loggedInAuthData?.role === "STAFF" ?  null : 
             <GuardedButton className="batch-edit-btn" icon={<EditOutlined />} onClick={() => setIsEditMode(true)}>Edit Profile</GuardedButton>  ) : (
            <Space>
              <Button icon={<CloseOutlined />} onClick={handleCancelEdit}>Cancel</Button>
              <Button type="primary" icon={<SaveOutlined />} loading={isUpdating || isUploadingImage} onClick={handleEditSave}>Save Changes</Button>
            </Space>
          ))}
        </div>
      </div>

      {/* Hero */}
      <div className="batch-hero">
        <Spin spinning={isUploadingImage} size="small" wrapperClassName="uv-avatar-upload-spinner">
          <Avatar size={60} src={getProfileImage()} style={{ flexShrink: 0 }} />
        </Spin>
        <div className="batch-hero-info">
          <div className="batch-hero-titlerow">
            <h2 className="batch-hero-name">{getDisplayName(user)}</h2>
            <span className="uv-role-tag" style={{ background: roleColors.bg, border: `1px solid ${roleColors.border}`, color: roleColors.text }}>{roleLabel}</span>
            <span className={`batch-status-pill batch-status-pill--${statusClass}`}>
              <span className="batch-status-dot" />
              {user?.status || 'ACTIVE'}
            </span>
          </div>
          <div className="batch-hero-meta">
            {user?.email && <span className="batch-hero-meta-item"><MailOutlined /> {user.email}</span>}
            {user?.phoneNumber && <span className="batch-hero-meta-item"><PhoneOutlined /> {user.phoneNumber}</span>}
            {user?.enrolledNumber && <span className="batch-hero-meta-item"><IdcardOutlined /> {user.enrolledNumber}</span>}
            {user?.address && <span className="batch-hero-meta-item"><EnvironmentOutlined /> {user.address}</span>}
          </div>
          {isEditMode && (
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <Upload showUploadList={false} beforeUpload={beforeUpload} accept="image/*">
                <Button size="small" icon={<CameraOutlined />}>{hasImage ? 'Change' : 'Upload'}</Button>
              </Upload>
              {hasImage && <Button size="small" icon={<DeleteOutlined />} danger onClick={handleDeleteImage}>Remove</Button>}
            </div>
          )}
        </div>
      </div>

      <div>
        <Form form={form} layout="vertical" disabled={!isEditMode} className="batch-view-form">
          <section className="uv-section uv-section--divider">
            <div className="uv-card-header">
              <RxAvatar className="uv-card-header-icon" />
              <span className="uv-card-header-title">Personal Details</span>
            </div>
            {isEditMode ? (
              <div className="uv-personal-grid">
                <div className="uv-pd-item">
                  <span className="uv-pd-label">First Name</span>
                  <Form.Item name="firstName" style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Please enter first name' }]}>
                    <Input placeholder="Enter first name" />
                  </Form.Item>
                </div>
                <div className="uv-pd-item">
                  <span className="uv-pd-label">Last Name</span>
                  <Form.Item name="lastName" style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Please enter last name' }]}>
                    <Input placeholder="Enter last name" />
                  </Form.Item>
                </div>
                <div className="uv-pd-item">
                  <span className="uv-pd-label">Date of Birth</span>
                  <Form.Item name="dateOfBirth" style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Please select date of birth' }]}>
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabledDate={(current) => current && current.isAfter(dayjs().subtract(18, 'year'))} />
                  </Form.Item>
                </div>
                <div className="uv-pd-item">
                  <span className="uv-pd-label">Gender</span>
                  <Form.Item name="gender" style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Please select gender' }]}>
                    <Select placeholder="Select gender">
                      <Select.Option value="Male">Male</Select.Option>
                      <Select.Option value="Female">Female</Select.Option>
                      <Select.Option value="Transgender">Transgender</Select.Option>
                    </Select>
                  </Form.Item>
                </div>
                <div className="uv-pd-item">
                  <span className="uv-pd-label">Address</span>
                  <Form.Item name="address" style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Please enter address' }]}>
                    <Input.TextArea placeholder="Enter address" rows={2} style={{ resize: 'none' }} />
                  </Form.Item>
                </div>
              </div>
            ) : (
              <div className="uv-personal-grid">
                <div className="uv-pd-item"><span className="uv-pd-label">First Name</span><span className="uv-pd-value">{user?.firstName || '-'}</span></div>
                <div className="uv-pd-item"><span className="uv-pd-label">Last Name</span><span className="uv-pd-value">{user?.lastName || '-'}</span></div>
                <div className="uv-pd-item"><span className="uv-pd-label">Date of Birth</span><span className="uv-pd-value">{user?.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString('en-GB') : '-'}</span></div>
                <div className="uv-pd-item"><span className="uv-pd-label">Gender</span><span className="uv-pd-value">{user?.gender || '-'}</span></div>
                <div className="uv-pd-item"><span className="uv-pd-label">Address</span><span className="uv-pd-value">{user?.address || '-'}</span></div>
              </div>
            )}
          </section>
        </Form>

        {/* Enrollments (students) */}
        {roleLabel === 'STUDENT' && (
          <section className="uv-section">
            <div className="batch-section-label">Enrollments</div>
            {enrolments.length > 0 ? (
              <div className="uv-enroll-wrap">
                <Table
                  className="uv-enroll-table"
                  dataSource={pagedEnrolments}
                  columns={enrollmentColumns}
                  rowKey={(r: any, i?: number) => r?._id ?? String(i)}
                  pagination={false}
                />
                <div className="uv-enroll-footer">
                  <Pagination
                    current={enrollPage}
                    pageSize={ENROLL_PAGE_SIZE}
                    total={enrolments.length}
                    onChange={setEnrollPage}
                    size="small"
                    showSizeChanger={false}
                  />
                  <span className="uv-enroll-count">{pagedEnrolments.length} of {enrolments.length} enrollments</span>
                </div>
              </div>
            ) : (
              <Empty description="No enrollments found" />
            )}
          </section>
        )}

        {/* Attendance & History (instructors) */}
        {roleLabel === 'INSTRUCTOR' && (
          <section className="uv-section">
            <Tabs
              defaultActiveKey="attendance"
              className="uv-instructor-tabs"
              items={[
                {
                  key: 'attendance',
                  label: (
                    <span className="uv-tab-label">
                      <CalendarOutlined />
                      Availability
                    </span>
                  ),
                  children: (
                    <AttendanceCalendarView
                      historyList={historyList}
                      user={user}
                      isLoading={isHistoryLoading}
                    />
                  ),
                },
                {
                  key: 'history',
                  label: (
                    <span className="uv-tab-label">
                      <HistoryOutlined />
                      Activity History
                    </span>
                  ),
                  children: (
                    <div className="bh2-page" style={{ padding: 0 }}>
                      <div className="bh2-topbar" style={{ padding: '0 0 16px 0' }}>
                        <div className="bh2-filter-chips">
                          {HISTORY_FILTER_CHIPS.map((chip) => (
                            <button
                              key={chip.key}
                              className={`bh2-chip ${historyActiveFilter === chip.key ? 'bh2-chip--active' : ''}`}
                              onClick={() => setHistoryActiveFilter(chip.key)}
                            >
                              {chip.label}
                            </button>
                          ))}
                        </div>
                        <Input
                          placeholder="Search events or batches…"
                          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                          value={historySearch}
                          onChange={(e) => setHistorySearch(e.target.value)}
                          className="bh2-search"
                          allowClear
                        />
                      </div>
                      <div className="bh2-body" style={{ padding: 0 }}>
                        <div className="bh2-timeline-col">
                          {isHistoryLoading ? (
                            <div className="bh2-loading"><Spin size="large" /></div>
                          ) : filteredHistory.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No history records found" style={{ margin: '48px 0' }} />
                          ) : (
                            <div className="bh2-timeline">
                              {filteredHistory.map((record, idx) => (
                                <HistoryTimelineItem
                                  key={record._id || idx}
                                  record={record}
                                  isLast={idx === filteredHistory.length - 1}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="bh2-sidebar">
                          <HistorySummaryCard list={historyList} />
                          <HistoryEventTypesCard list={historyList} />
                        </div>
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'ratings',
                  label: (
                    <span className="uv-tab-label">
                      <StarOutlined />
                      Ratings & Reviews
                    </span>
                  ),
                  children: (
                    <InstructorRatingsTab instructorId={userId} />
                  ),
                },
              ]}
            />
          </section>
        )}
      </div>
    </div>
  );
};

export default UserView;